// RUTA: src/components/fitness/RestTimer.tsx
//
// Modal expandido del cronómetro de descanso. SIN PROPS — lee TODO
// el estado del RestTimerContext (global). Se monta una sola vez al
// nivel del App.
//
// La sección que quiera arrancar un timer (RoutineSection, etc.)
// llama a `useRestTimer().openModal({ exerciseName, restOptions })`
// — no renderiza este componente directamente.

import { useEffect, useState } from 'react';
import { Timer, X, RotateCcw, Play, Pause, Plus, Minus, Minimize2, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useRestTimer, formatTime } from '@/contexts/RestTimerContext';
import { toast } from 'sonner';

const FIRST_TIME_TIP_KEY = 'iftmx_rest_timer_tip_seen';

export function RestTimer() {
  const t = useRestTimer();
  const [confirmCancel, setConfirmCancel] = useState(false);

  // First-time tip — la primera vez que el cliente abre un timer en
  // su vida (en cualquier dispositivo con localStorage), mostramos
  // un toast educativo sobre que puede minimizar.
  useEffect(() => {
    if (t.modalOpen) {
      try {
        if (!localStorage.getItem(FIRST_TIME_TIP_KEY)) {
          toast(
            '💡 Tip — Puedes ver otros ejercicios mientras descansas',
            {
              description: 'El cronómetro queda flotando arriba aunque salgas de aquí. Usa "Minimizar" para esconderlo sin detenerlo.',
              duration: 9000,
            },
          );
          localStorage.setItem(FIRST_TIME_TIP_KEY, '1');
        }
      } catch { /* ignore quota */ }
    }
  }, [t.modalOpen]);

  const progress = t.totalSeconds > 0 ? ((t.totalSeconds - t.remaining) / t.totalSeconds) * 100 : 0;
  const isFinished = t.started && t.remaining === 0;
  const isLast10 = t.remaining <= 10 && t.remaining > 0 && t.started;
  const hasCoachOptions = t.presets.length > 0 && t.presets[0].label !== '1:00';

  // Cierre con [×] cuando hay timer activo → confirmación
  const handleClose = () => {
    if (t.active) {
      setConfirmCancel(true);
    } else {
      t.cancel();
    }
  };

  return (
    <>
      <Dialog
        open={t.modalOpen}
        onOpenChange={(v) => {
          if (!v) {
            // Cerrar dialog desde overlay/esc → minimizar (no cancelar)
            t.minimize();
          }
        }}
      >
        {/* `[&>button[type=button]]:hidden` oculta el botón [×] que
         *  shadcn/ui Dialog inyecta automáticamente. Antes generaba
         *  un doble target visual junto a nuestro botón "Minimizar".
         *  Manejamos cierre con nuestros propios controles. */}
        <DialogContent className="max-w-sm p-0 overflow-hidden bg-card border-2 border-accent/30 [&>button[type=button]]:hidden">
          <DialogTitle className="sr-only">Temporizador de descanso</DialogTitle>
          <DialogDescription className="sr-only">Temporizador de descanso entre series de ejercicios</DialogDescription>

          {/* Header — usamos grid con columnas explícitas para que
           *  el botón derecho tenga ancho fijo y el título trunque
           *  cuando el nombre del ejercicio es muy largo. */}
          <div className="bg-primary p-4 grid items-center gap-2" style={{ gridTemplateColumns: '1fr auto' }}>
            <div className="flex items-center gap-2 min-w-0">
              <Timer className="text-accent shrink-0" size={20} />
              <span className="text-sm font-black uppercase tracking-widest text-primary-foreground shrink-0">
                Descanso
              </span>
              {t.exerciseName && (
                <span className="text-[10px] font-bold text-accent/70 uppercase tracking-wider truncate min-w-0">
                  · {t.exerciseName}
                </span>
              )}
            </div>
            <div className="flex items-center shrink-0">
              {/* Cuando el timer está ACTIVO mostramos SOLO el botón
               *  "Minimizar" (acción principal). Antes mostrábamos
               *  también un [×] al lado que se confundía visualmente
               *  con dos targets de cerrar. Para cancelar el timer
               *  activo el cliente usa "Reiniciar" en los controles. */}
              {t.active ? (
                <button
                  onClick={() => t.minimize()}
                  className="px-3 py-1.5 rounded-lg bg-primary-foreground/10 hover:bg-primary-foreground/20 text-primary-foreground flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider transition-colors min-h-[36px]"
                  aria-label="Minimizar (timer sigue corriendo)"
                  title="Minimizar — el timer seguirá corriendo arriba"
                >
                  <Minimize2 size={14} />
                  Minimizar
                </button>
              ) : (
                <button
                  onClick={handleClose}
                  className="text-primary-foreground/60 hover:text-primary-foreground p-2 -m-1"
                  aria-label="Cerrar"
                >
                  <X size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Countdown display */}
          <div className="px-6 pt-6 pb-2 text-center">
            <div className="relative w-48 h-48 mx-auto mb-4">
              <svg className="w-full h-full -rotate-90" viewBox="0 0 200 200">
                <circle cx="100" cy="100" r="88" fill="none" stroke="hsl(var(--secondary))" strokeWidth="8" />
                <circle
                  cx="100" cy="100" r="88"
                  fill="none"
                  stroke={isFinished ? 'hsl(var(--accent))' : isLast10 ? 'hsl(var(--destructive))' : 'hsl(var(--accent))'}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 88}`}
                  strokeDashoffset={`${2 * Math.PI * 88 * (1 - progress / 100)}`}
                  className="transition-all duration-500 ease-linear"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-5xl font-black tabular-nums tracking-tight ${
                  isFinished ? 'text-accent animate-pulse' : isLast10 ? 'text-destructive' : 'text-foreground'
                }`}>
                  {formatTime(t.remaining)}
                </span>
                {isFinished && (
                  <span className="text-xs font-bold uppercase tracking-widest text-accent mt-1">¡Listo!</span>
                )}
              </div>
            </div>

            {/* Texto educativo — refuerzo pasivo cuando timer está corriendo */}
            {t.active && !isFinished && (
              <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-1 mb-3 px-2">
                <Lightbulb className="w-3 h-3 shrink-0" />
                <span>El cronómetro sigue corriendo aunque salgas de aquí</span>
              </p>
            )}

            {/* Manual adjust — only shown when no coach options and not started */}
            {!t.started && !hasCoachOptions && (
              <div className="flex items-center justify-center gap-4 mb-4">
                <button
                  onClick={() => t.adjustSeconds(-10)}
                  className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-foreground hover:bg-border transition-colors"
                >
                  <Minus size={16} />
                </button>
                <span className="text-lg font-black tabular-nums w-16 text-center">{formatTime(t.totalSeconds)}</span>
                <button
                  onClick={() => t.adjustSeconds(10)}
                  className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center text-foreground hover:bg-border transition-colors"
                >
                  <Plus size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Presets */}
          <div className="px-6 pb-3">
            {hasCoachOptions && !t.started && (
              <p className="text-[10px] text-center text-muted-foreground mb-2 font-bold uppercase tracking-wider">
                Opciones definidas por tu Coach
              </p>
            )}
            <div className="flex gap-2 justify-center flex-wrap">
              {t.presets.map(p => (
                <button
                  key={p.seconds}
                  onClick={() => t.selectPreset(p.seconds)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all ${
                    t.totalSeconds === p.seconds && !t.started
                      ? 'bg-accent text-accent-foreground shadow-md'
                      : 'bg-secondary text-muted-foreground hover:bg-border'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="px-6 pb-6 flex flex-col gap-3">
            {t.alarming ? (
              <>
                <Button
                  onClick={() => t.stopAlarm()}
                  variant="destructive"
                  className="w-full font-black uppercase tracking-widest text-sm min-h-[48px] animate-pulse"
                >
                  <X size={16} /> Detener Alarma
                </Button>
                <Button onClick={() => t.reset()} variant="outline" className="w-full font-black uppercase tracking-widest text-sm min-h-[48px]">
                  <RotateCcw size={16} /> Reiniciar
                </Button>
              </>
            ) : !t.started ? (
              <Button onClick={() => t.start()} className="w-full font-black uppercase tracking-widest text-sm min-h-[48px]">
                <Play size={16} /> Iniciar
              </Button>
            ) : (
              <div className="flex gap-3">
                <Button
                  onClick={() => t.togglePause()}
                  variant={t.running ? 'secondary' : 'default'}
                  className="flex-1 font-black uppercase tracking-widest text-sm min-h-[48px]"
                >
                  {t.running ? <Pause size={16} /> : <Play size={16} />}
                  {t.running ? 'Pausar' : 'Reanudar'}
                </Button>
                <Button onClick={() => t.reset()} variant="outline" className="flex-1 font-black uppercase tracking-widest text-sm min-h-[48px]">
                  <RotateCcw size={16} /> Reiniciar
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmación de cancelar */}
      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent className="max-w-sm">
          <DialogTitle className="text-base font-black uppercase tracking-wide">¿Cancelar el cronómetro?</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Si lo cancelas se borra. Si solo quieres ver otros ejercicios mientras descansas, usa <span className="font-bold">"Minimizar"</span> — el timer seguirá corriendo arriba.
          </DialogDescription>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setConfirmCancel(false)} className="flex-1">
              Volver
            </Button>
            <Button variant="destructive" onClick={() => { t.cancel(); setConfirmCancel(false); }} className="flex-1">
              Sí, cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
